import { SettingsOutlined } from '@mui/icons-material';
import { FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { useAlertsStore, useUserStore } from '@/configs/store';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { useLocalStore } from '@/hooks/useLocalStore';
import { useToggleState } from '@/hooks/useToggleState';
import { trackerUrls } from '@/tracker/config/urls';
import { IssueList, IssueListControls, useIssueListSettings } from '@/tracker/features/issueList';
import { TaskFilterBar } from '@/tracker/features/taskFilters';
import { useTrackerStore } from '@/tracker/model';
import { FavoriteButton } from '@/tracker/ui/FavoriteButton';
import { Loader } from '@/tracker/ui/Loader';
import { LoadMoreTrigger } from '@/tracker/ui/LoadMoreTrigger';
import { PageNav } from '@/tracker/ui/PageNav';

import { QueueAccessDialog } from '../QueueAccessDialog/QueueAccessDialog';
import { QueueStore } from '../../model/QueueStore';

import s from './QueueContent.module.scss';

const QueueContent = observer(({ queueKey }: { queueKey: string }) => {
  const alertsStore = useAlertsStore();
  const trackerStore = useTrackerStore();
  const { store } = useLocalStore(() => new QueueStore({ alertsStore, trackerStore, queueKey }));
  const { isAdmin } = useUserStore();
  const access = useToggleState();
  const { settings, toggleColumn, setGroupBy } = useIssueListSettings({
    key: 'queue',
    defaults: {
      columns: ['priority', 'type', 'status', 'assignee', 'updateTime'],
      groupBy: 'none',
    },
  });

  useEffect(() => {
    store.load();
  }, [store]);

  const { queue } = store;
  const isEmpty = store.tasks.length === 0;

  const navigation = (
    <PageNav parent={{ label: 'Очереди', to: trackerUrls.queues }} current={queueKey} />
  );

  if (store.isNotFound) {
    return (
      <div className={s.page}>
        {navigation}
        <div className={s.page__empty}>
          <Typography color="text.secondary">
            Очередь {queueKey} не найдена или нет доступа
          </Typography>
        </div>
      </div>
    );
  }

  if (store.isError) {
    return (
      <div className={s.page}>
        {navigation}
        <div className={s.page__empty}>
          <Typography color="text.secondary">Не удалось загрузить очередь</Typography>
          <Button onClick={store.load}>Повторить</Button>
        </div>
      </div>
    );
  }

  if (!queue) {
    return (
      <div className={s.page}>
        {navigation}
        <Loader />
      </div>
    );
  }

  return (
    <div className={s.page}>
      {navigation}

      {/* Шапка и список проявляются вместе, когда очередь загрузилась */}
      <div className={s.page__body}>
        <div className={s.page__title}>
          <div className={s.page__heading}>
            <Typography variant="h5" fontWeight="bold">
              {queue.name}
            </Typography>
            <FavoriteButton
              isFavorite={trackerStore.isFavoriteQueue(queue.key)}
              onToggle={() => trackerStore.toggleFavoriteQueue(queue)}
            />
            {/* Участников очереди меняет только админ — как «Настройки очереди» в Яндекс Трекере */}
            {isAdmin && IS_V2_WRITE_ENABLED && (
              <Tooltip title="Доступ к очереди">
                <IconButton size="small" aria-label="Доступ к очереди" onClick={access.open}>
                  <SettingsOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={s.page__meta}>
            {queue.key} · Руководитель: {queue.lead?.displayName ?? '—'}
          </div>
          {queue.description && <div className={s.page__description}>{queue.description}</div>}
        </div>

        <IssueListControls
          query={store.query}
          onQueryChange={store.setQuery}
          counter={`${store.tasks.length} / ${store.totalSize}`}
          settings={settings}
          onGroupByChange={setGroupBy}
          onToggleColumn={toggleColumn}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={store.showClosed || store.filters.values.statuses.length > 0}
                disabled={store.filters.values.statuses.length > 0}
                onChange={(event) => store.setShowClosed(event.target.checked)}
              />
            }
            label="Показывать закрытые"
          />
        </IssueListControls>

        <div className={s.page__filters}>
          <TaskFilterBar
            model={store.filters}
            fields={['statuses', 'types', 'priorities', 'assignees', 'authors', 'sprints']}
            statuses={queue.statuses}
            types={queue.taskTypes}
          />
        </div>

        {isEmpty && !store.isTasksLoaded && !store.isTasksError && <Loader />}

        {store.isTasksError && isEmpty && (
          <div className={s.page__empty}>
            <Typography color="text.secondary">Не удалось загрузить задачи</Typography>
            <Button onClick={store.loadTasks}>Повторить</Button>
          </div>
        )}

        {store.isTasksLoaded && isEmpty && (
          <div className={s.page__empty}>
            <Typography color="text.secondary">
              {store.query ? 'Ничего не нашлось' : 'Задач нет'}
            </Typography>
          </div>
        )}

        {!isEmpty && (
          <>
            <IssueList
              issues={store.tasks}
              settings={settings}
              sort={store.sort}
              onSortChange={store.setSort}
              isLoading={store.isTasksLoading}
            />

            <div className={s.page__footer}>
              <Typography variant="body2" color="text.secondary">
                Показано {store.tasks.length} из {store.totalSize}
              </Typography>
              <LoadMoreTrigger
                hasMore={store.hasMore}
                isLoading={store.isTasksLoading}
                isError={store.isTasksError}
                onLoadMore={store.loadMore}
              />
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {access.opened && <QueueAccessDialog queue={queue} onClose={access.close} />}
      </AnimatePresence>
    </div>
  );
});

export { QueueContent };
